import { bosdyn_api_msgs } from 'rclnodejs';

import {
  ArmCommand_Feedback,
  NamedArmPositionsCommand_Feedback,
  NamedArmPositionsCommand_Feedback_Status,
  namedArmPositionsCommand_Feedback_StatusToJSON,
} from 'lib/generated/proto/bosdyn/api/arm_command';
import {
  ArmDragCommand_Feedback,
  ArmDragCommand_Feedback_Status,
  RobotCommandFeedbackStatus_Status,
  armDragCommand_Feedback_StatusToJSON,
  robotCommandFeedbackStatus_StatusToJSON,
} from 'lib/generated/proto/bosdyn/api/basic_command';
import {
  ClawGripperCommand_Feedback,
  ClawGripperCommand_Feedback_Status,
  GripperCommand_Feedback,
  clawGripperCommand_Feedback_StatusToJSON,
} from 'lib/generated/proto/bosdyn/api/gripper_command';
import { RobotCommandFeedback } from 'lib/generated/proto/bosdyn/api/robot_command';
import { SynchronizedCommand_Feedback } from 'lib/generated/proto/bosdyn/api/synchronized_command';

import { logBlue } from 'utils/debugger';

// todo - they can be all replaced with bosdyn_api_msgs.msg.[...]
import {
  IRosTypeBosdynApiMsgsArmCommandFeedback,
  IRosTypeBosdynApiMsgsArmDragCommandFeedback,
  IRosTypeBosdynApiMsgsGripperCommandFeedback,
  IRosTypeBosdynApiMsgsNamedArmPositionsCommandFeedback,
  IRosTypeBosdynApiMsgsRobotCommandFeedbackStatusStatus,
} from 'types/generated/ros-msgs';

/**
 * Not very useful currently - for debugging and errors properly
 *
 * Docs: https://dev.bostondynamics.com/protos/bosdyn/api/proto_reference#robotcommandfeedbackstatus-status
 *
 * STATUS_UNKNOWN	0	Behavior execution is in an unknown / unexpected state.
 * STATUS_PROCESSING	1	The robot is actively working on the command
 * STATUS_COMMAND_OVERRIDDEN	2	The command was replaced by a new command
 * STATUS_COMMAND_TIMED_OUT	3	The command expired
 * STATUS_ROBOT_FROZEN	4	The robot is in an unsafe state, and will only respond to known safe commands.
 * STATUS_INCOMPATIBLE_HARDWARE	5	The request cannot be executed because the required hardware is missing.
 *  For example, an armless robot receiving a synchronized command with an arm_command
 *  request will return this value in the arm_command_feedback status.
 */
function parseRobotCommandFeedbackStatus(
  status: IRosTypeBosdynApiMsgsRobotCommandFeedbackStatusStatus,
) {
  return status.value as RobotCommandFeedbackStatus_Status;
}

function parseGripperCommandFeedback(
  gripper_command_feedback: IRosTypeBosdynApiMsgsGripperCommandFeedback,
) {
  if (!gripper_command_feedback) return;
  // general feedback for the command
  const gripperStatus = parseRobotCommandFeedbackStatus(gripper_command_feedback.status);

  // Feedback for the claw gripper command.
  const { claw_gripper_feedback } = gripper_command_feedback.command;

  /**
   * docs: https://dev.bostondynamics.com/protos/bosdyn/api/proto_reference#clawgrippercommand-feedback-status
   * STATUS_UNKNOWN	0	STATUS_UNKNOWN should never be used. If used, an internal error has happened.
   * STATUS_IN_PROGRESS	1	The gripper is opening or closing.
   * STATUS_AT_GOAL	2	The gripper is at the final point of the trajectory.
   * STATUS_APPLYING_FORCE	3	During a close, detected contact and transitioned to force control.
   */
  const clawStatus = claw_gripper_feedback?.status.value as ClawGripperCommand_Feedback_Status;

  const clawGripperFeedback = ClawGripperCommand_Feedback.create({
    status: clawStatus,
  });

  return GripperCommand_Feedback.create({
    status: gripperStatus,
    clawGripperFeedback,
  });
}

/**
 * Docs: https://dev.bostondynamics.com/protos/bosdyn/api/proto_reference#namedarmpositionscommand-feedback-status
 *
 * STATUS_UNKNOWN	0	STATUS_UNKNOWN should never be used. If used, an internal error has happened.
 * STATUS_COMPLETE	1	The arm is at the desired configuration.
 * STATUS_IN_PROGRESS	2	Robot is re-configuring arm to get to desired configuration.
 * STATUS_STALLED_HOLDING_ITEM	3	Some requests may not execute if the gripper is holding an item declared not
 *  stowable, e.g. POSITIONS_STOW with carry_state == CARRY_STATE_CARRIABLE. In these
 *  situations, Spot will instead run an ArmStopCommand request while the blocking
 *  condition remains true. Clearing the condition will cause the request to proceed and
 *  the arm will start moving.
 */
function parseNamedArmPositionCommandFeedback(
  named_arm_position_feedback: IRosTypeBosdynApiMsgsNamedArmPositionsCommandFeedback,
) {
  if (!named_arm_position_feedback) return;
  const namedArmPositionStatus = named_arm_position_feedback.status
    .value as NamedArmPositionsCommand_Feedback_Status;

  return NamedArmPositionsCommand_Feedback.create({
    status: namedArmPositionStatus,
  });
}

function parseArmDragFeedback(arm_drag_feedback: IRosTypeBosdynApiMsgsArmDragCommandFeedback) {
  if (!arm_drag_feedback) return;

  const armDragFeedback = arm_drag_feedback.status.value as ArmDragCommand_Feedback_Status;

  return ArmDragCommand_Feedback.create({
    status: armDragFeedback,
  });
}

function parseArmCommandFeedback(arm_command_feedback: IRosTypeBosdynApiMsgsArmCommandFeedback) {
  if (!arm_command_feedback) return;

  const { named_arm_position_feedback, arm_drag_feedback } = arm_command_feedback.feedback;
  const namedArmPositionFeedback = parseNamedArmPositionCommandFeedback(
    named_arm_position_feedback,
  );

  const armDragFeedback = parseArmDragFeedback(arm_drag_feedback);
  // note - its always 0 but currently also nobody is using it
  const armCommandStatus = parseRobotCommandFeedbackStatus(arm_command_feedback.status);

  return ArmCommand_Feedback.create({
    namedArmPositionFeedback,
    armDragFeedback,
    status: armCommandStatus,
  });
}

function parseSynchronizedFeedback(
  synchronized_feedback: bosdyn_api_msgs.msg.SynchronizedCommandFeedback,
) {
  const { gripper_command_feedback, arm_command_feedback } = synchronized_feedback;
  const gripperCommandFeedback = parseGripperCommandFeedback(gripper_command_feedback);
  const armCommandFeedback = parseArmCommandFeedback(arm_command_feedback);

  return SynchronizedCommand_Feedback.create({
    gripperCommandFeedback,
    armCommandFeedback,
  });
}

/**
 * This parse the ros message to protobuf
 *
 * @param feedback
 */
export function parseRobotCommandFeedback(feedback: bosdyn_api_msgs.msg.RobotCommandFeedback) {
  // logGreen('parseRobotCommandFeedback:feedback', feedback);
  const feedBack = feedback.command.synchronized_feedback;
  const synchronizedFeedback = parseSynchronizedFeedback(feedBack);

  return RobotCommandFeedback.create({ synchronizedFeedback });
}

/**
 * "Result" stands for feedback and repose - it is the same data structure
 */
export class RobotCommandResultParser {
  /**
   * Not sure if needed or not
   * Docs: {@link https://dev.bostondynamics.com/protos/bosdyn/api/proto_reference#robotcommandfeedbackstatus-status}
   */
  static getArmCommandStatus(data: bosdyn_api_msgs.msg.RobotCommandFeedback) {
    const robotCommandFeedback = parseRobotCommandFeedback(data);
    const status = robotCommandFeedback.synchronizedFeedback?.armCommandFeedback?.status || 0;
    const statusString = robotCommandFeedbackStatus_StatusToJSON(status);
    logBlue('getCommandStatus', statusString);
    return statusString;
  }

  /**
   * Value is the same as getArmCommandStatus
   * @param data
   */
  static getGripperCommandStatus(data: bosdyn_api_msgs.msg.RobotCommandFeedback) {
    const robotCommandFeedback = parseRobotCommandFeedback(data);
    const status = robotCommandFeedback.synchronizedFeedback?.gripperCommandFeedback?.status || 0;

    const statusString = robotCommandFeedbackStatus_StatusToJSON(status);
    logBlue('getGripperCommandStatus', statusString);
    return statusString;
  }

  static getDragCommandStatus(data: bosdyn_api_msgs.msg.RobotCommandFeedback) {
    const robotCommandFeedback = parseRobotCommandFeedback(data);
    // todo - maybe later in the parser function
    logBlue('🤖🤖🤖robot command: result', robotCommandFeedback);
    const status =
      robotCommandFeedback.synchronizedFeedback?.armCommandFeedback?.armDragFeedback?.status || 0;
    const statusString = armDragCommand_Feedback_StatusToJSON(status);
    logBlue('getDragCommandStatus', statusString);
    return statusString;
  }

  /**
   * Both, carry and stow are the same command type
   * @param data
   */
  static getArmCarryStowStatus(data: bosdyn_api_msgs.msg.RobotCommandFeedback) {
    const robotCommandFeedback = parseRobotCommandFeedback(data);
    // todo - maybe later in the parser function
    logBlue('🤖🤖🤖robot command: result', robotCommandFeedback);
    const status =
      robotCommandFeedback.synchronizedFeedback?.armCommandFeedback?.namedArmPositionFeedback
        ?.status || 0;
    const statusString = namedArmPositionsCommand_Feedback_StatusToJSON(status);
    logBlue('getArmCarryStowStatus', statusString);
    return statusString;
  }

  static getClawGripperStatus(data: bosdyn_api_msgs.msg.RobotCommandFeedback) {
    const robotCommandFeedback = parseRobotCommandFeedback(data);
    // todo - maybe later in the parser function
    logBlue('🤖🤖🤖robot command: result', robotCommandFeedback);

    const status =
      robotCommandFeedback.synchronizedFeedback?.gripperCommandFeedback?.clawGripperFeedback
        ?.status || 0;
    const statusString = clawGripperCommand_Feedback_StatusToJSON(status);
    logBlue('getClawGripperStatus', statusString);
    return statusString;
  }

  static getArmStopStatus(data: bosdyn_api_msgs.msg.RobotCommandFeedback) {
    const robotCommandFeedback = parseRobotCommandFeedback(data);
    // todo - maybe later in the parser function
    logBlue('🤖🤖🤖robot command: result', robotCommandFeedback);

    return 'no status for stop implemented by bd';
  }
}
